import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import WhatsAppPanelShell from './WhatsAppPanelShell';
import WhatsAppDevicePreview from './WhatsAppDevicePreview';
import WhatsAppCampaignRetargetSection from './WhatsAppCampaignRetargetSection';
import WhatsAppCampaignBatchesTab from './WhatsAppCampaignBatchesTab';
import { buildPreviewMessage } from './whatsappPreview';
import { devicePreviewFromTemplate } from './whatsappTemplatePreview';
import { inputClass } from './whatsappCampaignFormUtils';
import { StatCard, StatusBadge, EmptyState } from './whatsappCampaignUi';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';
import { WHATSAPP_BASE_PATH } from '../../models/MasterAdmin/whatsappHub';
import type { WhatsAppCampaign, WhatsAppTemplate } from '../../models/MasterAdmin/whatsappCampaign';
import {
  CAMPAIGN_TYPE_LABELS,
  SEND_MODE_LABELS,
  SERVICE_VERTICAL_LABELS,
} from '../../models/MasterAdmin/whatsappCampaign';
import {
  enqueueCampaignMetaLimitRetarget,
  enqueueCampaignRcsRetarget,
  fetchApprovedWhatsAppTemplates,
  fetchCampaignMetaLimitRetries,
  fetchCampaignOutboundBatches,
  fetchCampaignRcsRetries,
  fetchWhatsAppCampaignAnalysis,
  fetchOutboundBatchDetail,
  getWhatsAppCampaign,
  retryFailedWhatsAppCampaign,
  stopCampaignMetaLimitRetarget,
  stopCampaignRcsRetarget,
  updateCampaignMetaLimitSchedule,
  updateCampaignRcsSchedule,
  type WhatsAppCampaignAnalysis,
  type WhatsAppOutboundBatchDetail,
  type WhatsAppOutboundBatchSummary,
} from '../../services/MasterAdmin/whatsappCampaignService';

type TabId = 'overview' | 'batches' | 'meta' | 'rcs' | 'participants';
type ParticipantFilterId =
  | 'attempted'
  | 'sent'
  | 'delivered'
  | 'read'
  | 'replied'
  | 'meta'
  | 'other'
  | 'failed'
  | 'excluded'
  | 'skipped'
  | 'non_reader'
  | 'deferred';

const TABS: { id: TabId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'batches', label: 'Batches' },
  { id: 'meta', label: 'Meta WA retarget' },
  { id: 'rcs', label: 'RCS retarget' },
  { id: 'participants', label: 'Participants' },
];

const FILTER_LABELS: Record<ParticipantFilterId, string> = {
  attempted: 'Attempted',
  sent: 'Sent',
  delivered: 'Delivered',
  read: 'Read',
  replied: 'Replied',
  meta: 'Limited by Meta',
  other: 'Other failures',
  failed: 'Failed',
  excluded: 'Excluded',
  skipped: 'Weekly cap skip',
  non_reader: 'Non-readers excluded',
  deferred: 'Scheduled next week',
};

const WhatsAppCampaignDetailPage: React.FC = () => {
  const { campaignId = '' } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabId>('overview');
  const [campaign, setCampaign] = useState<WhatsAppCampaign | null>(null);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [analysis, setAnalysis] = useState<WhatsAppCampaignAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [retryLoading, setRetryLoading] = useState(false);
  const [participantQuery, setParticipantQuery] = useState('');
  const [activeBatchDetail, setActiveBatchDetail] = useState<WhatsAppOutboundBatchDetail | null>(null);
  const [outboundBatches, setOutboundBatches] = useState<WhatsAppOutboundBatchSummary[]>([]);
  const [metaData, setMetaData] = useState<Awaited<ReturnType<typeof fetchCampaignMetaLimitRetries>>['data'] | null>(null);
  const [rcsData, setRcsData] = useState<Awaited<ReturnType<typeof fetchCampaignRcsRetries>>['data'] | null>(null);
  const [retargetLoading, setRetargetLoading] = useState(false);
  const [participantFilter, setParticipantFilter] = useState<ParticipantFilterId | null>(null);
  const [participantsLoading, setParticipantsLoading] = useState(false);

  const loadRetarget = useCallback(async () => {
    if (!campaignId) return;
    setRetargetLoading(true);
    try {
      const [metaRes, rcsRes] = await Promise.all([
        fetchCampaignMetaLimitRetries(campaignId, { limit: 100 }).catch(() => null),
        fetchCampaignRcsRetries(campaignId, { limit: 100, logLimit: 100 }).catch(() => null),
      ]);
      setMetaData(metaRes?.data || null);
      setRcsData(rcsRes?.data || null);
    } finally {
      setRetargetLoading(false);
    }
  }, [campaignId]);

  const loadParticipants = useCallback(
    async (filter?: ParticipantFilterId | null) => {
      if (!campaignId) return;
      setParticipantsLoading(true);
      try {
        const res = await fetchWhatsAppCampaignAnalysis(campaignId, {
          limit: 200,
          lite: false,
          participantFilter: filter || undefined,
        });
        setAnalysis(res.data);
        setParticipantFilter(filter || null);
      } catch (err: unknown) {
        toast.error(getApiErrorMessage(err, 'Failed to load participants'));
      } finally {
        setParticipantsLoading(false);
      }
    },
    [campaignId]
  );

  const handleViewList = useCallback((filter: ParticipantFilterId) => {
    setParticipantFilter(filter);
    setTab('participants');
  }, []);

  const activeOutboundBatch = useMemo(
    () => outboundBatches.find((b) => b.status === 'PENDING' || b.status === 'RUNNING') || null,
    [outboundBatches]
  );

  const load = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    setAnalysisLoading(true);
    try {
      const [campRes, tplRes, batchesRes, metaRes, rcsRes] = await Promise.all([
        getWhatsAppCampaign(campaignId),
        fetchApprovedWhatsAppTemplates(),
        fetchCampaignOutboundBatches(campaignId, { limit: 30 }),
        fetchCampaignMetaLimitRetries(campaignId, { limit: 100 }).catch(() => null),
        fetchCampaignRcsRetries(campaignId, { limit: 100, logLimit: 100 }).catch(() => null),
      ]);
      setCampaign(campRes.data);
      setTemplates(tplRes.data || []);
      const batches = batchesRes.data || [];
      setOutboundBatches(batches);
      setMetaData(metaRes?.data || null);
      setRcsData(rcsRes?.data || null);
      setLoading(false);

      const active =
        batches.find((b) => b.status === 'PENDING' || b.status === 'RUNNING') || batches[0] || null;
      const [detailRes, liteRes] = await Promise.all([
        active?.id ? fetchOutboundBatchDetail(active.id).catch(() => null) : Promise.resolve(null),
        fetchWhatsAppCampaignAnalysis(campaignId, { limit: 40, lite: true }),
      ]);
      if (detailRes?.data) setActiveBatchDetail(detailRes.data);
      setAnalysis(liteRes.data);
      setAnalysisLoading(false);

      if (!active || !['PENDING', 'RUNNING'].includes(String(active.status || ''))) {
        void fetchWhatsAppCampaignAnalysis(campaignId, { limit: 100, lite: false })
          .then((full) => setAnalysis(full.data))
          .catch(() => undefined);
      }
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to load campaign'));
      setLoading(false);
      setAnalysisLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (tab !== 'participants' || !campaignId) return;
    void loadParticipants(participantFilter);
  }, [tab, campaignId, participantFilter, loadParticipants]);

  useEffect(() => {
    const batch = activeBatchDetail?.batch;
    if (!campaignId || !batch || !['PENDING', 'RUNNING'].includes(String(batch.status || ''))) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const [campRes, detailRes, liteRes, batchesRes] = await Promise.all([
          getWhatsAppCampaign(campaignId),
          fetchOutboundBatchDetail(batch.id),
          fetchWhatsAppCampaignAnalysis(campaignId, { limit: 40, lite: true }),
          fetchCampaignOutboundBatches(campaignId, { limit: 30 }),
        ]);
        if (cancelled) return;
        setCampaign(campRes.data);
        setActiveBatchDetail(detailRes.data);
        setOutboundBatches(batchesRes.data || []);
        setAnalysis(liteRes.data);
      } catch {
        /* ignore */
      }
    };
    const timer = window.setInterval(tick, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [campaignId, activeBatchDetail?.batch?.id, activeBatchDetail?.batch?.status]);

  const filteredParticipants = useMemo(
    () =>
      (analysis?.participants || []).filter((p) => {
        const q = participantQuery.trim().toLowerCase();
        if (!q) return true;
        return (
          p.name.toLowerCase().includes(q) ||
          p.phone.toLowerCase().includes(q) ||
          String(p.status || '').toLowerCase().includes(q) ||
          (p.bodyValues || []).some((v) => String(v).toLowerCase().includes(q))
        );
      }),
    [analysis?.participants, participantQuery]
  );

  const sampleBodyValues =
    analysis?.participants?.find((p) => p.bodyValues?.length)?.bodyValues || campaign?.bodyValues || [];

  const handleRetryFailed = async () => {
    if (!campaignId) return;
    setRetryLoading(true);
    try {
      const res = await retryFailedWhatsAppCampaign(campaignId);
      toast.success(res.message || 'Retry queued');
      await load();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Retry failed'));
    } finally {
      setRetryLoading(false);
    }
  };

  if (!campaignId) {
    return (
      <WhatsAppPanelShell title="Campaign">
        <p className="text-sm text-gray-500">Invalid campaign ID</p>
      </WhatsAppPanelShell>
    );
  }

  return (
    <WhatsAppPanelShell
      title={campaign?.name || 'Campaign details'}
      description={
        campaign
          ? `${CAMPAIGN_TYPE_LABELS[campaign.campaignType || 'BROADCAST']} · ${campaign.templateName}`
          : 'Loading…'
      }
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`${WHATSAPP_BASE_PATH}/campaigns`}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back
          </Link>
          <button
            type="button"
            disabled={loading}
            onClick={() => void load()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {campaign && (
            <button
              type="button"
              onClick={() => navigate(`${WHATSAPP_BASE_PATH}/campaigns/${campaignId}/edit`)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#25D366] px-3 py-2 text-sm font-medium text-white"
            >
              <PencilSquareIcon className="h-4 w-4" />
              Edit
            </button>
          )}
        </div>
      }
    >
      {loading && !campaign ? (
        <p className="py-20 text-center text-sm text-gray-500">Loading campaign…</p>
      ) : !campaign ? (
        <p className="py-20 text-center text-sm text-gray-500">Campaign not found</p>
      ) : (
        <>
          <div className="mb-6 flex flex-wrap gap-2 border-b border-gray-200 pb-3 dark:border-gray-700">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                  tab === t.id
                    ? 'bg-[#25D366]/15 text-[#128C7E] dark:text-[#25D366]'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                }`}
              >
                {t.label}
                {t.id === 'batches' && activeOutboundBatch && (
                  <span className="ml-1.5 inline-flex h-2 w-2 animate-pulse rounded-full bg-[#009de2]" title="Send in progress" />
                )}
                {t.id === 'meta' && (metaData?.schedule?.isScheduled || (metaData?.stats?.PENDING || 0) > 0) && (
                  <span className="ml-1.5 inline-flex h-2 w-2 rounded-full bg-amber-500" title="Active queue" />
                )}
                {t.id === 'rcs' && (rcsData?.schedule?.isScheduled || (rcsData?.stats?.PENDING || 0) > 0) && (
                  <span className="ml-1.5 inline-flex h-2 w-2 rounded-full bg-violet-500" title="Active queue" />
                )}
              </button>
            ))}
          </div>

          {tab === 'overview' && (
            <div className="mb-6 space-y-4">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
                <StatCard
                  label="Attempted"
                  value={analysis?.analysis.attempted ?? campaign.recipientCount ?? 0}
                  onViewList={() => handleViewList('attempted')}
                />
                <StatCard
                  label="Sent"
                  value={analysis?.analysis.sent ?? campaign.totalSent ?? 0}
                  onViewList={() => handleViewList('sent')}
                />
                <StatCard
                  label="Delivered"
                  value={analysis?.analysis.delivered ?? campaign.totalDelivered ?? 0}
                  hint={`${analysis?.analysis.deliveryRatePct ?? 0}%`}
                  onViewList={() => handleViewList('delivered')}
                />
                <StatCard
                  label="Read"
                  value={analysis?.analysis.read ?? campaign.totalRead ?? 0}
                  hint={`${analysis?.analysis.readRatePct ?? 0}%`}
                  onViewList={() => handleViewList('read')}
                />
                <StatCard
                  label="Replied"
                  value={analysis?.analysis.replied ?? 0}
                  hint={`${analysis?.analysis.replyRatePct ?? 0}%`}
                  onViewList={() => handleViewList('replied')}
                />
                <StatCard
                  label="Limited by Meta"
                  value={analysis?.analysis.limitedByMeta ?? 0}
                  hint="Interakt-style"
                  onViewList={() => handleViewList('meta')}
                />
                <StatCard
                  label="Other failures"
                  value={analysis?.analysis.otherFailures ?? 0}
                  onViewList={() => handleViewList('other')}
                />
              </div>

              {analysis?.policy?.policyCounts && campaign.messageCategory === 'MARKETING' && (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                  <StatCard
                    label="Excluded"
                    value={
                      (analysis.policy.policyCounts.EXCLUDED_NON_READER || 0) +
                      (analysis.policy.policyCounts.SKIPPED || 0)
                    }
                    hint="Non-readers + weekly cap"
                    onViewList={() => handleViewList('excluded')}
                  />
                  <StatCard
                    label="Non-readers excluded"
                    value={analysis.policy.policyCounts.EXCLUDED_NON_READER || 0}
                    onViewList={() => handleViewList('non_reader')}
                  />
                  <StatCard
                    label="Weekly cap skip"
                    value={analysis.policy.policyCounts.SKIPPED || 0}
                    onViewList={() => handleViewList('skipped')}
                  />
                  <StatCard
                    label="Scheduled next week"
                    value={analysis.policy.policyCounts.DEFERRED_WEEKLY || 0}
                    onViewList={() => handleViewList('deferred')}
                  />
                  <StatCard
                    label="Failed"
                    value={
                      analysis.policy.policyCounts.FAILED ||
                      analysis?.analysis.failed ||
                      0
                    }
                    onViewList={() => handleViewList('failed')}
                  />
                </div>
              )}
            </div>
          )}

          {tab === 'overview' && (
            <div className="space-y-6">
              {analysisLoading && (
                <p className="text-xs text-gray-500">Refreshing stats…</p>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800/50">
                <div>
                  <p className="font-medium">Est. cost: ₹{analysis?.analysis.estimatedCostInr?.toFixed?.(2) ?? '0.00'}</p>
                  <p className="text-xs text-gray-500">{campaign.messageCategory || 'MARKETING'} rate</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={retryLoading || ['PENDING', 'RUNNING'].includes(String(activeBatchDetail?.batch?.status || ''))}
                    onClick={() => void handleRetryFailed()}
                    className="rounded-lg bg-[#009de2] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60"
                  >
                    {retryLoading ? 'Queuing…' : 'Retry failed / unsent'}
                  </button>
                  {(analysis?.interakt?.dashboardUrl || campaign.interaktMeta?.interaktApiCampaignDashboardUrl) && (
                    <a
                      href={analysis?.interakt?.dashboardUrl || campaign.interaktMeta?.interaktApiCampaignDashboardUrl || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-medium text-[#009de2] hover:underline"
                    >
                      Open in Interakt →
                    </a>
                  )}
                </div>
              </div>

              {activeOutboundBatch && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#009de2]/30 bg-[#009de2]/5 px-4 py-3">
                  <div>
                    <p className="text-sm font-medium">
                      Outbound send in progress · {activeOutboundBatch.progressPct}% complete
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {activeOutboundBatch.totalAttempted}/{activeOutboundBatch.totalQueued} attempted ·{' '}
                      {activeOutboundBatch.totalSent} sent
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setTab('batches')}
                    className="rounded-lg bg-[#009de2] px-3 py-1.5 text-xs font-medium text-white"
                  >
                    View batches
                  </button>
                </div>
              )}

              <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
                <dl className="space-y-2 rounded-xl border p-4 text-sm dark:border-gray-700">
                  <div><dt className="text-xs text-gray-500">Status</dt><dd><StatusBadge value={campaign.campaignType === 'AUTOMATED' ? campaign.automationStatus : campaign.status} /></dd></div>
                  <div><dt className="text-xs text-gray-500">Service</dt><dd>{SERVICE_VERTICAL_LABELS[campaign.serviceVertical || ''] || '—'}</dd></div>
                  <div><dt className="text-xs text-gray-500">Send mode</dt><dd>{SEND_MODE_LABELS[campaign.sendMode || 'MANUAL']}</dd></div>
                  <div><dt className="text-xs text-gray-500">Campaign ID</dt><dd className="break-all font-mono text-xs">{campaign.id}</dd></div>
                </dl>
                {(() => {
                  const tpl = templates.find((t) => t.name === campaign.templateName);
                  const preview = tpl
                    ? { ...devicePreviewFromTemplate(tpl, { headerMediaUrl: campaign.headerMediaUrl, headerMimeType: campaign.headerMediaUrl ? 'image/jpeg' : null }), message: buildPreviewMessage(tpl.bodyPreview || '', sampleBodyValues) }
                    : { message: buildPreviewMessage('', sampleBodyValues), headerText: null, footer: null, buttons: [], headerMediaUrl: campaign.headerMediaUrl, headerMimeType: null };
                  return (
                    <div className="flex flex-col items-center">
                      <p className="mb-2 text-xs font-semibold uppercase text-gray-500">Preview</p>
                      <WhatsAppDevicePreview {...preview} contactName="Vedika.Health" />
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {tab === 'batches' && <WhatsAppCampaignBatchesTab campaignId={campaignId} />}

          {tab === 'meta' && (
            <WhatsAppCampaignRetargetSection
              variant="meta"
              campaignId={campaignId}
              eligibleCount={Number(analysis?.analysis.limitedByMeta) || 0}
              stats={metaData?.stats || null}
              schedule={metaData?.schedule || null}
              items={metaData?.items || []}
              analysis={metaData?.analysis || analysis?.analysis.metaLimited || null}
              attemptAnalysis={metaData?.attemptAnalysis || null}
              loading={retargetLoading || analysisLoading}
              showWhenEmpty
              onRefresh={loadRetarget}
              onEnqueue={async (mode, sendAt) => {
                await enqueueCampaignMetaLimitRetarget(campaignId, { mode, sendAt });
              }}
              onUpdateSchedule={async (mode, sendAt) => {
                await updateCampaignMetaLimitSchedule(campaignId, { mode, sendAt });
              }}
              onStopNextBatch={async () => {
                const res = await stopCampaignMetaLimitRetarget(campaignId);
                return { cancelled: Number(res.data?.cancelled) || 0 };
              }}
            />
          )}

          {tab === 'rcs' && (
            <WhatsAppCampaignRetargetSection
              variant="rcs"
              campaignId={campaignId}
              eligibleCount={Number(analysis?.analysis.otherFailures) || 0}
              stats={rcsData?.stats || null}
              schedule={rcsData?.schedule || null}
              items={rcsData?.items || []}
              logs={rcsData?.logs || []}
              loading={retargetLoading || analysisLoading}
              showWhenEmpty
              onRefresh={loadRetarget}
              onEnqueue={async (mode, sendAt, options) => {
                if (mode === 'policy24h') return;
                await enqueueCampaignRcsRetarget(campaignId, {
                  mode,
                  sendAt,
                  rcsTemplateName: options?.rcsTemplateName,
                });
              }}
              onUpdateSchedule={async (mode, sendAt) => {
                await updateCampaignRcsSchedule(campaignId, { mode, sendAt });
              }}
              onStopNextBatch={async () => {
                const res = await stopCampaignRcsRetarget(campaignId);
                return { cancelled: Number(res.data?.cancelled) || 0 };
              }}
            />
          )}

          {tab === 'participants' && (
            <div className="rounded-xl border dark:border-gray-700">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3 dark:border-gray-800">
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className="font-semibold">Participants ({analysis?.participantTotal ?? 0})</h4>
                  {participantFilter ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-medium text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200">
                      {FILTER_LABELS[participantFilter]}
                      <button
                        type="button"
                        className="ml-0.5 hover:text-indigo-950"
                        onClick={() => setParticipantFilter(null)}
                        aria-label="Clear filter"
                      >
                        ×
                      </button>
                    </span>
                  ) : null}
                </div>
                <input
                  value={participantQuery}
                  onChange={(e) => setParticipantQuery(e.target.value)}
                  placeholder="Search…"
                  className={`${inputClass} max-w-xs`}
                  disabled={(participantsLoading || analysisLoading) && !analysis}
                />
              </div>
              <div className="max-h-[70vh] overflow-auto">
                {(participantsLoading || analysisLoading) && !analysis?.participants?.length ? (
                  <EmptyState title="Loading participants…" description="Fetching delivery analysis for this campaign." />
                ) : filteredParticipants.length === 0 ? (
                  <div className="p-4">
                    <EmptyState
                      title={
                        participantQuery.trim()
                          ? 'No participants match your search'
                          : (analysis?.participantTotal ?? 0) === 0
                            ? 'No participants yet'
                            : 'No matching participants'
                      }
                      description={
                        participantQuery.trim()
                          ? 'Try a different name, phone, status, or variable value.'
                          : 'Participants appear after the campaign is sent and delivery logs are available. Refresh if a send just finished.'
                      }
                    />
                  </div>
                ) : (
                  <table className="min-w-full text-sm">
                    <thead className="sticky top-0 bg-gray-50 text-xs uppercase dark:bg-gray-800">
                      <tr>
                        <th className="px-3 py-2 text-left">Participant</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Variables</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredParticipants.map((p) => (
                        <tr key={String(p.id)} className="border-t align-top dark:border-gray-800">
                          <td className="px-3 py-2">
                            <p className="font-medium">{p.name}</p>
                            <p className="text-xs text-gray-500">{p.phone}</p>
                            {p.errorMessage && <p className="mt-1 text-[11px] text-red-600">{p.errorMessage}</p>}
                          </td>
                          <td className="px-3 py-2">
                            <StatusBadge value={p.status} />
                          </td>
                          <td className="px-3 py-2 text-xs">{(p.bodyValues || []).join(' · ') || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </WhatsAppPanelShell>
  );
};

export default WhatsAppCampaignDetailPage;
